import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import { BorderGridContainer, GridBox, Hidden, Image } from '@ahmdigital/ui';
import { connect } from 'react-redux';
import { find, flow, get, head, values } from 'lodash/fp';
import { PRODUCT, PRODUCT_LIMIT_CATEGORY } from '@ahmdigital/constants';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import styled from 'styled-components';

import { BasicDetailsModalFormValues } from './components/basic-details-form';
import { CHOOSABLE_PRODUCTS, SHORT_PAGE_TITLE_TEMPLATE } from '../../../../ahm-constants/ui';
import { Customer } from '../../../../selectors/select-customer/types';
import { determineBundlesFromProductId, determineProductIdFromBundles } from './utils/bundle';
import { LimitGroup } from './types';
import { ProductWithServicesType } from '../../../../types/product';
import AmbulanceSection from './components/ambulance-section';
import BaseBundleSection from './components/base-bundle-section';
import BasicDetailsModal from './components/basic-details-modal';
import BundlesSection from './components/bundles-section';
import ChickenDinnerSection from './components/chicken-dinner-section';
import ChoosableActions from '../../../../actions/choosable';
import CustomerActions from '../../../../actions/customer';
import FooterActions from '../../../../actions/footer';
import getChoosableAllIncludedServices from './utils/get-choosable-all-included-services';
import handRightImage from '../../../../images/hand-right.svg';
import Head from '../../../../../utils/head';
import HeaderSection from './components/header-section';
import JoinBar from './components/join-bar';
import LoadingSpinner from '../../../../components/loading-spinner';
import makeSelectHasAllRequiredProductsLoaded from '../../../../selectors/make-select-has-all-required-products-loaded';
import OfferBanner from '../../../../components/offers/banner';
import PriceActions from '../../../../actions/price';
import PriceService, { Price } from '../../../../models/price';
import PricingDrawer from '../../../../components/pricing-drawer';
import ProductActions from '../../../../actions/product';
import selectChoosableComparerProductId from '../../../../selectors/select-choosable-comparer-product-id';
import selectCustomer from '../../../../selectors/select-customer';
import selectEffectiveDateOverride from '../../../../selectors/select-effective-date-override';
import selectIsPremiumChangeEnabled from '../../../../selectors/select-is-premium-change-enabled';
import selectTargetSegment from '../../../../selectors/select-target-segment';
import SnakeSeparator from './components/snake-separator';
import type { RootState } from '../../../../reducers';

const Wrapper = styled.div`
  overflow-x: hidden;
`;

const SnakeWithHandWrapper = styled.div`
  position: relative;
`;

const HandImage = styled(Image)`
  max-width: 215px;
  position: absolute;
  right: -30px;
  top: 6px;
  transform: scaleX(-1);
`;

type PickerPageProps = {
  actions: {
    choosable: typeof ChoosableActions;
    customer: typeof CustomerActions;
    footer: typeof FooterActions;
    price: typeof PriceActions;
    product: typeof ProductActions;
  };
  areAllProductsLoaded: boolean;
  choosableComparerProductId?: string;
  customer: Pick<Customer, 'frequency' | 'hasSetBasicDetails' | 'scale' | 'state'>;
  effectiveDate?: string;
  prices?: Record<string, Price>;
  products?: Record<string, ProductWithServicesType> | undefined;
  targetSegment: string;
};

const PickerPage = ({
  actions,
  areAllProductsLoaded,
  choosableComparerProductId = undefined,
  customer,
  effectiveDate = undefined,
  prices = undefined,
  products = undefined,
  targetSegment,
}: PickerPageProps) => {
  const { hasSetBasicDetails } = customer;
  const [isModalOpen, setIsModalOpen] = useState(!hasSetBasicDetails);

  // @ts-expect-error - Automatic, Please fix when editing this file
  const { search } = useRouter();
  const searchParams = new URLSearchParams(search);
  const modal = searchParams.get('modal');

  useEffect(() => {
    if (modal === 'open') {
      setIsModalOpen(true);
    }
  }, [modal]);

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const defaultValues = { frequency: customer.frequency, scale: customer.scale, state: customer.state };
  const handleSubmitBasicDetailsForm = ({ frequency, scale, state }: BasicDetailsModalFormValues) => {
    if (frequency !== defaultValues.frequency) {
      // @ts-expect-error - BasicDetailsModalFormValues makes frequency optional, we should be checking for undefined before passing them to changeFrequency
      actions.customer.changeFrequency(frequency);
    }
    if (scale !== defaultValues.scale) {
      // @ts-expect-error - BasicDetailsModalFormValues makes scale optional, we should be checking for undefined before passing them to changeFrequency and changeScale
      actions.customer.changeScale(scale);
    }
    if (state !== defaultValues.state) {
      // @ts-expect-error - BasicDetailsModalFormValues makes state optional, we should be checking for undefined before passing them to changeState
      actions.customer.changeState(state);
    }

    closeModal();
  };

  const [selectedBundles, setSelectedBundles] = useState<string[]>([]);

  const handleBundleSelection = (bundles: string[]) => {
    setSelectedBundles(bundles);
    actions.choosable.setChoosableComparerProductId(determineProductIdFromBundles(bundles));
  };

  useEffect(() => {
    actions.product.load(CHOOSABLE_PRODUCTS);
  }, [actions]);

  // Use this key composed of relevant customer data to indicate when customer data
  // changes and to hence trigger the useEffect to load prices
  const customerKey = PriceService.getKey('', customer, { date: effectiveDate });
  useEffect(() => {
    actions.price.load(CHOOSABLE_PRODUCTS, { customer }, { date: effectiveDate });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actions, customerKey, effectiveDate]);

  useEffect(() => {
    if (areAllProductsLoaded) {
      setSelectedBundles(determineBundlesFromProductId(choosableComparerProductId));
    }
  }, [areAllProductsLoaded, choosableComparerProductId]);

  const allIncludedServices = getChoosableAllIncludedServices(products);

  const teethBundleServices = find({ limitId: PRODUCT_LIMIT_CATEGORY.TEETH }, allIncludedServices);

  const choosable50IncludedServices: LimitGroup[] = products?.[PRODUCT.CHOOSABLE_50_TEETH]?.extras?.included || [];
  const choosable50TeethServices = find({ limitId: PRODUCT_LIMIT_CATEGORY.TEETH }, choosable50IncludedServices);

  return (
    <>
      <Head titleTemplate={SHORT_PAGE_TITLE_TEMPLATE}>
        <title>ahm choosable | health insurance extras</title>
        <meta
          name="description"
          content="Build your extras health cover around what you know you need, and only ever pay for the extras bundles you’ll use. Start with the base bundle, then tailor from there."
        />
      </Head>
      <BasicDetailsModal
        form={{ defaultValues, handleSubmit: handleSubmitBasicDetailsForm }}
        isOpen={isModalOpen}
        toggleModal={closeModal}
      />
      <BorderGridContainer hideBorderTop>
        <OfferBanner
          filterProductId={choosableComparerProductId || flow(values, head, get('id'))(products)}
          targetSegment={targetSegment}
        />

        <GridBox padding="0">
          <HeaderSection />
          {!areAllProductsLoaded ? (
            <LoadingSpinner />
          ) : (
            <Wrapper>
              <SnakeSeparator />
              <BaseBundleSection
                {...{
                  includedServices: selectedBundles.length > 0 ? teethBundleServices : choosable50TeethServices,
                  isBundleChosen: selectedBundles.length > 0,
                  scale: customer.scale,
                }}
              />
              <AmbulanceSection />
              <SnakeWithHandWrapper>
                <SnakeSeparator />
                <Hidden xs sm lg df>
                  <HandImage src={handRightImage.src} alt="Illustration of a hand" />
                </Hidden>
              </SnakeWithHandWrapper>
              <BundlesSection
                {...{
                  allIncludedServices,
                  scale: customer.scale,
                  selectedBundles,
                  setSelectedBundles: handleBundleSelection,
                }}
              />
              <ChickenDinnerSection />
            </Wrapper>
          )}
        </GridBox>
      </BorderGridContainer>
      <PricingDrawer isProductWithHospitalServices={false} showIncomeOptions />
      {areAllProductsLoaded && <JoinBar {...{ customer, effectiveDate, prices, selectedBundles }} />}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  areAllProductsLoaded: makeSelectHasAllRequiredProductsLoaded(CHOOSABLE_PRODUCTS)(state),
  choosableComparerProductId: selectChoosableComparerProductId(state),
  customer: selectCustomer(state),
  effectiveDate: selectEffectiveDateOverride(state),
  isPremiumChangeEnabled: selectIsPremiumChangeEnabled(state),
  prices: state.price.data,
  products: state.product.data,
  targetSegment: selectTargetSegment(state),
  useFutureEffectiveDate: state.price.useFutureEffectiveDate,
});

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => ({
  actions: {
    choosable: bindActionCreators(ChoosableActions, dispatch),
    customer: bindActionCreators(CustomerActions, dispatch),
    footer: bindActionCreators(FooterActions, dispatch),
    price: bindActionCreators(PriceActions, dispatch),
    product: bindActionCreators(ProductActions, dispatch),
  },
});

// @ts-expect-error - Automatic, Please fix when editing this file
export default connect(mapStateToProps, mapDispatchToProps)(PickerPage);

export { mapDispatchToProps, mapStateToProps, PickerPage as PickerPageForTest };
